"use client";
import React, { useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useIntl } from "react-intl";
import dynamic from "next/dynamic";
import Link from "next/link";

import { toast } from "react-toastify";

import { Button } from "@components/ui/button";

import { useJob } from "../_context/JobContext";
import { useSession } from "next-auth/react";
import { isUserHasUploadedCv } from "@actions/cvActions";
import Spinner from "@components/ui/Spinner";

const Modal = dynamic(() => import("@components/Modals/Modal"), { ssr: false });

interface JobTranslations {
  title: string;
  "login-text": string;
  "btn-login": string;
  "btn-cancel": string;
  "profile-approval-text": string;
  "upload-cv-btn": string;
}

type ModalState = "closed" | "login" | "profileApproval";

const Apply = () => {
  // Hooks
  const session = useSession();

  const intl = useIntl();
  const pathname = usePathname();
  const { isApplied, applyForJob } = useJob();

  const [isLoading, setIsLoading] = useState(false);

  const [modalState, setModalState] = useState<ModalState>("closed");

  // Refs
  const loginLinkRef = useRef<HTMLAnchorElement>(null);
  const editProfileLinkRef = useRef<HTMLAnchorElement>(null);

  const translation = intl.messages["job-details"] as any;

  const detailTranslation = translation["details-section"];

  const jobApplyTranslation = translation?.["job-apply"] ?? {};
  const modalTranslation = jobApplyTranslation?.["modal"] as JobTranslations;

  const messagesTranslation = jobApplyTranslation?.["messages"] as Record<
    string,
    string
  >;

  const handleApply = async () => {
    if (!session?.data?.user?.id) {
      // setLoginModalOpen(true);
      setModalState("login");

      return null;
    }

    if (session?.data?.user?.role !== "CANDIDATE") return;

    const isApplying = !isApplied;
    setIsLoading(true);

    const userHasCv = await isUserHasUploadedCv();
    if (!userHasCv) {
      setModalState("profileApproval");
      setIsLoading(false);

      return;
    }

    try {
      const success = await applyForJob();

      if (success)
        toast.success(
          messagesTranslation?.[`${!isApplying ? "un" : ""}apply-success`]
        );
      else {
        toast.error(
          messagesTranslation?.[`${!isApplying ? "un" : ""}apply-error`]
        );
      }
    } catch (err) {
      toast.error(
        messagesTranslation?.[`${!isApplying ? "un" : ""}apply-error`]
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginModal = (value: boolean) => {
    setModalState(value ? "login" : "closed");
  };

  const handleProfileApprovalModal = (value: boolean) => {
    setModalState(value ? "profileApproval" : "closed");
  };

  return (
    <>
      <Link href={`/login?callbackUrl=${pathname}`} hidden ref={loginLinkRef} />
      <Link href={`/candidate/profile/cv?callbackUrl=${pathname}`} hidden ref={editProfileLinkRef} />

      <Modal
        title={modalTranslation?.title}
        description={modalTranslation?.["login-text"]}
        open={modalState === "login"}
        setOpen={handleLoginModal}
        primaryText={modalTranslation?.["btn-login"]}
        secondaryText={modalTranslation?.["btn-cancel"]}
        primaryClick={() => loginLinkRef?.current?.click()}
      />

      <Modal
        title={modalTranslation?.title}
        description={modalTranslation?.["profile-approval-text"]}
        open={modalState === "profileApproval"}
        setOpen={handleProfileApprovalModal}
        secondaryText={modalTranslation?.["btn-cancel"]}
        primaryText={modalTranslation?.["upload-cv-btn"]}
        primaryClick={() => editProfileLinkRef?.current?.click()}
      />
      <Button
        disabled={isLoading}
        onClick={handleApply}
        className="w-full"
        variant="secondary"
      >
        {/* {isApplied
          ? detailTranslation?.["un-apply"]
          : detailTranslation?.["apply-now"]} */}
        {isLoading ? (
          <span className="flex items-center">
            <div className="mr-2.5">
              <Spinner className="w-6 h-6 border-l-white" />
            </div>
            {isApplied ? "Withdrawing..." : "Applying..."}
          </span>
        ) : isApplied ? (
          detailTranslation?.["un-apply"]
        ) : (
          detailTranslation?.["apply-now"]
        )}
      </Button>
    </>
  );
};

export default Apply;
